import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useRouter } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Code,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertCircle, IconArrowLeft, IconHelpCircle } from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import {
  getLotMovements,
  getLotTrace,
  type LotMovementDto,
  type LotOnHandDto,
  type LotTraceCustomerDto,
} from './traceApi';

/**
 * One lot's trace — where it came from, where it is, and who got it.
 *
 * **The recall question is the one this screen exists for**, so "who received it" is above the
 * movement list rather than buried under it. A customer the system cannot vouch for is marked
 * rather than dropped or silently mixed in: a wave that shipped before the split recorded lots
 * can only say those orders were on the same wave, and a recall list that reads as certain
 * throughout is worse than one that admits the difference.
 *
 * **Everything is in the session's warehouse.** A warehouse is the 3PL client boundary and the
 * trace does not cross it.
 */
export function LotTraceRoute() {
  const { t } = useTranslation();
  const router = useRouter();
  const { id } = useParams({ strict: false }) as { id: string };
  const warehouseId = useCurrentWarehouseId();
  const [page, setPage] = useState(1);

  const trace = useQuery({
    queryKey: ['lot-trace', id, warehouseId] as const,
    queryFn: ({ signal }) => getLotTrace(id, signal),
    enabled: Boolean(warehouseId && id),
  });

  const movements = useQuery({
    queryKey: ['lot-trace', id, warehouseId, 'movements', page] as const,
    queryFn: ({ signal }) => getLotMovements(id, { pageNumber: page }, signal),
    enabled: Boolean(warehouseId && id),
  });

  const customerColumns: DataColumn<LotTraceCustomerDto>[] = useMemo(() => [
    {
      key: 'customer',
      label: t('lotTrace.customer'),
      card: 'title',
      render: (c) => (
        <Stack gap={0}>
          <Group gap="xs">
            <Text size="sm" fw={500}>{c.customerName}</Text>
            {/* Said out loud. The alternative is a recall list that reads as
                certain all the way down. */}
            {!c.isExact && (
              <Badge size="sm" color="orange" variant="light"
                leftSection={<IconHelpCircle size={12} />}>
                {t('lotTrace.maybe')}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">{c.customerNumber}</Text>
        </Stack>
      ),
    },
    {
      key: 'order',
      label: t('lotTrace.order'),
      card: 'subtitle',
      render: (c) => (
        <Stack gap={0}>
          <Code>{c.orderNumber}</Code>
          {c.customerPo ? <Text size="xs" c="dimmed">{c.customerPo}</Text> : null}
        </Stack>
      ),
    },
    {
      key: 'shipped',
      label: t('lotTrace.shipped'),
      card: 'aside',
      render: (c) => (c.shippedAtUtc
        ? <Text size="sm">{new Date(c.shippedAtUtc).toLocaleDateString()}</Text>
        : <Text size="sm" c="dimmed">—</Text>),
    },
    {
      key: 'quantity',
      label: t('lotTrace.quantity'),
      align: 'right',
      render: (c) => (c.quantity === null
        ? <Text size="sm" c="dimmed">{t('lotTrace.unknownQuantity')}</Text>
        : <Text size="sm">{c.quantity}</Text>),
    },
  ], [t]);

  const onHandColumns: DataColumn<LotOnHandDto>[] = useMemo(() => [
    { key: 'warehouse', label: t('lotTrace.warehouse'), card: 'subtitle',
      render: (r) => r.warehouseCode },
    { key: 'location', label: t('lotTrace.location'), card: 'title',
      render: (r) => <Code>{r.locationCode}</Code> },
    { key: 'bucket', label: t('lotTrace.bucket'),
      render: (r) => r.balanceTypeName ?? r.balanceTypeCode ?? '—' },
    { key: 'plate', label: t('lotTrace.plate'),
      render: (r) => r.licensePlateNumber ?? <Text size="sm" c="dimmed">—</Text> },
    { key: 'quantity', label: t('lotTrace.quantity'), align: 'right', card: 'aside',
      render: (r) => `${r.quantity} ${r.uomCode}` },
  ], [t]);

  const movementColumns: DataColumn<LotMovementDto>[] = useMemo(() => [
    {
      key: 'when',
      label: t('lotTrace.when'),
      card: 'subtitle',
      render: (m) => <Text size="sm">{new Date(m.performedAtUtc).toLocaleString()}</Text>,
    },
    {
      key: 'what',
      label: t('lotTrace.what'),
      card: 'title',
      render: (m) => (
        <Stack gap={0}>
          <Group gap="xs">
            <Text size="sm">{t(`lotTrace.doc.${m.documentType.toLowerCase()}`, m.documentType)}</Text>
            {m.fromWave && <Badge size="xs" variant="light" color="grape">{t('lotTrace.wave')}</Badge>}
          </Group>
          {m.documentNumber ? <Code>{m.documentNumber}</Code> : null}
        </Stack>
      ),
    },
    {
      key: 'who',
      label: t('lotTrace.counterparty'),
      render: (m) => (
        <Stack gap={0}>
          <Text size="sm">{m.counterparty ?? '—'}</Text>
          {m.poNumber ? <Text size="xs" c="dimmed">{m.poNumber}</Text> : null}
          {m.orderNumber ? <Text size="xs" c="dimmed">{m.orderNumber}</Text> : null}
        </Stack>
      ),
    },
    {
      key: 'where',
      label: t('lotTrace.location'),
      render: (m) => <Code>{m.locationCode}</Code>,
    },
    {
      key: 'quantity',
      label: t('lotTrace.quantity'),
      align: 'right',
      card: 'aside',
      // Signed as the ledger holds it: a minus is stock leaving, and hiding the
      // sign makes a receipt and a shipment look the same.
      render: (m) => (
        <Text size="sm" c={m.quantityDelta < 0 ? 'red' : undefined}>
          {m.quantityDelta} {m.uomCode}
        </Text>
      ),
    },
  ], [t]);

  if (!warehouseId) {
    return (
      <Stack>
        <PageHeader title={t('lotTrace.title')} />
        <Alert icon={<IconAlertCircle size={16} />} color="yellow">{t('lots.chooseWarehouse')}</Alert>
      </Stack>
    );
  }

  if (trace.isLoading) {
    return (
      <Stack>
        <PageHeader title={t('lotTrace.title')} />
        <Center py="xl"><Loader color="invetrak" /></Center>
      </Stack>
    );
  }

  if (trace.isError || !trace.data) {
    return (
      <Stack>
        <PageHeader title={t('lotTrace.title')} />
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {trace.error instanceof ApiError
            ? trace.error.problem.title
            : t('lotTrace.errors.loadFailed')}
        </Alert>
      </Stack>
    );
  }

  const s = trace.data.summary;
  const rows = movements.data?.rows ?? [];

  return (
    <>
      <PageHeader
        title={t('lotTrace.titleFor', { lot: s.lotNumber })}
        actions={
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => router.history.back()}
          >
            {t('shell.back')}
          </Button>
        }
      />

      <Stack gap="lg">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={4}>{s.itemNumber}</Title>
            {s.isExpired && (
              <Badge color="red" variant="light">{t('lots.expired')}</Badge>
            )}
          </Group>
          {s.itemDescription ? <Text c="dimmed">{s.itemDescription}</Text> : null}
          <Text size="sm" c="dimmed">
            {t('lotTrace.datesLine', {
              fifo: new Date(s.fifoDateUtc).toLocaleDateString(),
              expiry: s.expirationDateUtc
                ? new Date(s.expirationDateUtc).toLocaleDateString()
                : t('lotTrace.noExpiry'),
            })}
          </Text>
          {s.supplierLotNumber
            ? <Text size="sm" c="dimmed">{t('lots.supplierLot')}: {s.supplierLotNumber}</Text>
            : null}
        </Stack>

        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="sm">
          <Stat label={t('lotTrace.received')} value={`${s.quantityReceived} ${s.stockingUomCode}`} />
          <Stat label={t('lotTrace.shippedTotal')} value={`${s.quantityShipped} ${s.stockingUomCode}`} />
          <Stat label={t('lotTrace.adjusted')} value={`${s.quantityAdjusted} ${s.stockingUomCode}`} />
          <Stat label={t('lotTrace.onHand')} value={`${s.quantityOnHand} ${s.stockingUomCode}`} />
        </SimpleGrid>

        {/* The recall question, first. */}
        <Stack gap="xs">
          <Title order={5}>{t('lotTrace.whoGotIt')}</Title>
          {trace.data.customers.length === 0 ? (
            <Alert color="gray" variant="light">{t('lotTrace.nobodyYet')}</Alert>
          ) : (
            <>
              <ResponsiveTable
                rows={trace.data.customers}
                columns={customerColumns}
                rowKey={(c) => `${c.salesOrderId}-${c.isExact}`}
                minWidth={640}
                striped
              />
              {trace.data.customers.some((c) => !c.isExact) && (
                <Alert color="orange" variant="light" icon={<IconHelpCircle size={16} />}>
                  {t('lotTrace.maybeExplained')}
                </Alert>
              )}
            </>
          )}
        </Stack>

        <Stack gap="xs">
          <Title order={5}>{t('lotTrace.whereItIs')}</Title>
          {trace.data.onHand.length === 0 ? (
            <Alert color="gray" variant="light">{t('lotTrace.noneLeft')}</Alert>
          ) : (
            <ResponsiveTable
              rows={trace.data.onHand}
              columns={onHandColumns}
              rowKey={(r) => `${r.locationId}-${r.licensePlateNumber ?? ''}`}
              minWidth={640}
              striped
            />
          )}
        </Stack>

        <Stack gap="xs">
          <Title order={5}>{t('lotTrace.everything')}</Title>
          {movements.isLoading && <Center py="md"><Loader size="sm" color="invetrak" /></Center>}
          {movements.isSuccess && rows.length === 0 && (
            <Alert color="gray" variant="light">{t('lotTrace.noMovements')}</Alert>
          )}
          {rows.length > 0 && (
            <>
              <ResponsiveTable
                rows={rows}
                columns={movementColumns}
                rowKey={(m) => m.inventoryTransactionId}
                minWidth={820}
                striped
              />
              {/* Said plainly, as on the lot list: a page of 200 out of 900 is not
                  the whole answer, and this is the screen where that matters. */}
              {movements.data!.totalCount > rows.length && (
                <Group justify="space-between">
                  <Text size="xs" c="dimmed">
                    {t('lotTrace.showing', {
                      shown: rows.length,
                      total: movements.data!.totalCount,
                    })}
                  </Text>
                  <Group gap="xs">
                    <Button size="xs" variant="default" disabled={page === 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}>
                      {t('lotTrace.previous')}
                    </Button>
                    <Button size="xs" variant="default"
                      disabled={page * 200 >= movements.data!.totalCount}
                      onClick={() => setPage((p) => p + 1)}>
                      {t('lotTrace.next')}
                    </Button>
                  </Group>
                </Group>
              )}
            </>
          )}
        </Stack>
      </Stack>
    </>
  );
}

/**
 * One figure from the summary.
 *
 * Labelled as a group so the number can be read back WITH its label: "40" on its own is
 * ambiguous on a screen that also lists a movement of -40, and the two mean opposite things.
 */
function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card withBorder padding="sm" radius="md" role="group" aria-label={label}>
      <Text size="xs" c="dimmed" tt="uppercase">{label}</Text>
      <Text size="xl" fw={600}>{value}</Text>
    </Card>
  );
}
